// This is intended to be an abstract class. Extend this class and implement
// the getAccessToken() to get the API bearer token and async onError() to run
// when an API call returns an error.
export default class FetchWrapper {
  static CONTENT_TYPE_JSON = 'application/json'
  static CONTENT_TYPE_FORM_ENCODED = 'application/x-www-form-urlencoded'
  static AUTH_SCHEME_NONE = ''
  static AUTH_SCHEME_BASIC = 'Basic '
  static AUTH_SCHEME_BEARER = 'Bearer '

  constructor(
    baseURI,
    contentType = FetchWrapper.CONTENT_TYPE_JSON,
    authScheme = FetchWrapper.AUTH_SCHEME_BEARER) {
    // TODO: Add deafult tries and delay.

    if (this.constructor === FetchWrapper) {
      throw new Error('Class is of abstract type and can not be instantiated.')
    }

    if (this.getAccessToken === undefined || this.onError === undefined) {
      throw new Error('Class requires getAccessToken and onError to be implemented.')
    }

    this.baseURI = baseURI
    this.contentType = contentType
    this.authScheme = authScheme
    this.accessToken = this.getAccessToken()
  }

  prepareBody(data) {
    switch(this.contentType) {
      case FetchWrapper.CONTENT_TYPE_JSON:
        return JSON.stringify(data)
      case FetchWrapper.CONTENT_TYPE_FORM_ENCODED:
        return new URLSearchParams(data)
      default:
        throw new Error('Unsupported content type.')
    }
  }

  wait(delay) {
    return new Promise((resolve) => setTimeout(resolve, delay))
  }

  setBaseURI(baseURI) {
    const oldURI = this.baseURI
    this.baseURI = baseURI
    return oldURI
  }

  setAccessToken(newToken) {
    const oldToken = this.accessToken
    this.accessToken = newToken
    return oldToken
  }

  async get(path, tries = 2, delay = 500) {
    const response = await this.call(`${this.baseURI}${path}`, {
      method: 'GET',
      headers: {
        ...(this.accessToken && {'Authorization': this.authScheme + this.accessToken}),
      },
    }, tries, delay)
    .then(response => {
      return response
    })
    .catch(error => {
      throw error
    })
    return response
  }

  async put(path, data = null, tries = 2, delay = 500) {
    const response = await this.call(`${this.baseURI}${path}`, {
      method: 'PUT',
      body: data ? this.prepareBody(data) : null,
      headers: {
        ...(this.accessToken && {'Authorization': this.authScheme + this.accessToken}),
        ...(this.contentType && {'Content-Type': this.contentType}),
      },
    }, tries, delay)
    .then(response => {
      return response
    })
    .catch(error => {
      throw error
    })
    return response
  }

  async post(path, data = null, tries = 2, delay = 500) {
    const response = await this.call(`${this.baseURI}${path}`, {
      method: 'POST',
      body: data ? this.prepareBody(data) : null,
      headers: {
        ...(this.accessToken && {'Authorization': this.authScheme + this.accessToken}),
        ...(this.contentType && {'Content-Type': this.contentType}),
      },
    }, tries, delay)
    .then(response => {
      return response
    })
    .catch(error => {
      throw error
    })
    return response
  }

  async patch(path, data = null, tries = 2, delay = 500) {
    const response = await this.call(`${this.baseURI}${path}`, {
      method: 'PATCH',
      body: data ? this.prepareBody(data) : null,
      headers: {
        ...(this.accessToken && {'Authorization': this.authScheme + this.accessToken}),
        ...(this.contentType && {'Content-Type': this.contentType}),
      },
    }, tries, delay)
    .then(response => {
      return response
    })
    .catch(error => {
      throw error
    })
    return response
  }

  async delete(path, tries = 2, delay = 500) {
    const response = await this.call(`${this.baseURI}${path}`, {
      method: 'DELETE',
      headers: {
        ...(this.accessToken && {'Authorization': this.authScheme + this.accessToken}),
      },
    }, tries, delay)
    .then(response => {
      return response
    })
    .catch(error => {
      throw error
    })
    return response
  }

  async call(url, options = {}, tries = 2, delay = 500) {
    return fetch(url, options)
      .then(response => {
        if (!response.ok) {
          let error = new Error("HTTP status code: " + response.status)
          error.response = response
          error.status = response.status
          throw error
        }
        return response.json()
      })
      .catch(error => {
        return this.onError(error)
          .then(() => {
            // Retry the call.
            const triesLeft = tries - 1
            if (!triesLeft) {
              throw error
            }
            return this.wait(delay).then(() => this.call(url, options, triesLeft, delay))
          })
          .catch(error => {
            return Promise.reject(error)
          })
      })
  }
}